import { useEffect, useState, useCallback, useRef, type ReactNode, type JSX } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { PWAContext } from './PWAContext'
import { PushPrompt } from './PushPrompt'

// Тип для BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface PWAProviderProps {
    children: ReactNode
}

export function PWAProvider({ children }: PWAProviderProps): JSX.Element {
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine)
    const [isInstalled, setIsInstalled] = useState<boolean>(false)
    const [canInstall, setCanInstall] = useState<boolean>(false)
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [queueLength] = useState<number>(0)
    const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle')
    
    // Используем ref для избежания синхронного setState в эффекте
    const isInstalledRef = useRef<boolean>(false)

    // Регистрация Service Worker
    const {
        needRefresh: [updateAvailable],
        offlineReady: [serviceWorkerReady],
        updateServiceWorker
    } = useRegisterSW({
        onRegisteredSW(swUrl: string, r: ServiceWorkerRegistration | undefined) {
            console.log('SW Registered:', swUrl)
            // Проверка обновлений каждые 60 минут
            if (r) {
                setInterval(() => {
                void r.update()
                }, 60 * 60 * 1000)
            }
        },
        onRegisterError(error: unknown) {
        console.error('SW registration error:', error)
        }
    })

    // Отслеживание онлайн/офлайн
    useEffect(() => {
        const handleOnline = (): void => setIsOnline(true)
        const handleOffline = (): void => setIsOnline(false)

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [])

    // Проверка установки (в отдельном эффекте с микротаской)
    useEffect(() => {
        // Используем requestAnimationFrame чтобы избежать синхронного setState
        requestAnimationFrame(() => {
            if (window.matchMedia('(display-mode: standalone)').matches) {
                isInstalledRef.current = true
                setIsInstalled(true)
            }
        })
    }, [])

    // Перехват beforeinstallprompt
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event): void => {
            e.preventDefault()
            setDeferredPrompt(e as BeforeInstallPromptEvent)
            setCanInstall(true)
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        }
    }, [])

    // Установка PWA
    const install = useCallback(async (): Promise<void> => {
        if (!deferredPrompt) return
        
        await deferredPrompt.prompt()
        const { outcome } = await deferredPrompt.userChoice
        
        if (outcome === 'accepted') {
            setIsInstalled(true)
            setCanInstall(false)
        }
        setDeferredPrompt(null)
    }, [deferredPrompt])

    // Применение обновления SW
    const applyUpdate = useCallback((): void => {
        void updateServiceWorker(true)
    }, [updateServiceWorker])

    // Фоновая синхронизация при возвращении онлайн
    useEffect(() => {
        if (!isOnline) return

        // Используем микротаску для избежания синхронного setState
        Promise.resolve().then(() => {
            setSyncStatus('syncing')
        })

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(registration => {
                if ('sync' in registration) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const syncRegistration = registration as any
                    syncRegistration.sync.register('executive-queue').catch(() => {
                        setSyncStatus('error')
                    })
                }
                setSyncStatus('idle')
            }).catch(() => {
                setSyncStatus('error')
            })
        }
    }, [isOnline])

    const value = {
        isOnline,
        isInstalled,
        canInstall,
        updateAvailable,
        serviceWorkerReady,
        install,
        applyUpdate,
        syncStatus,
        queueLength
    }

    return (
        <PWAContext.Provider value={value}>
            {children}
            <PWAUI 
                updateAvailable={updateAvailable} 
                onUpdate={applyUpdate}
                isOnline={isOnline}
                canInstall={canInstall}
                onInstall={install}
            />
            <PushPrompt />
        </PWAContext.Provider>
    )
}

// UI компоненты для PWA
interface PWAUIProps {
    updateAvailable: boolean
    onUpdate: () => void
    isOnline: boolean
    canInstall: boolean
    onInstall: () => Promise<void>
}

function PWAUI({ updateAvailable, onUpdate, isOnline, canInstall, onInstall }: PWAUIProps): JSX.Element | null {
    const [showInstall, setShowInstall] = useState<boolean>(false)

    useEffect(() => {
        if (!canInstall) return
        
        const timer = setTimeout(() => setShowInstall(true), 3000)
        return () => clearTimeout(timer)
    }, [canInstall])

    return (
        <>
            {/* Индикатор офлайн */}
            {!isOnline && (
                <div className="fixed top-0 left-0 right-0 bg-red-600 text-white px-4 py-2 text-center z-50 animate-pulse">
                    <span className="font-medium">⚠️ Офлайн-режим</span>
                    <span className="ml-2 text-sm opacity-90">— показываются последние сохранённые данные</span>
                </div>
            )}

            {/* Уведомление об обновлении */}
            {updateAvailable && (
                <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-6 py-4 rounded-lg shadow-2xl z-50 flex items-center gap-4">
                    <div>
                        <p className="font-medium">Доступно обновление</p>
                        <p className="text-sm opacity-90">Новые метрики и улучшения</p>
                    </div>
                    <button
                        type="button"
                        onClick={onUpdate}
                        className="bg-white text-blue-600 px-4 py-2 rounded font-medium hover:bg-blue-50 transition"
                    >
                        Обновить
                    </button>
                </div>
            )}

            {/* Предложение установки */}
            {showInstall && canInstall && (
                <div className="fixed bottom-4 left-4 bg-gray-900 text-white px-6 py-4 rounded-lg shadow-2xl z-50 flex items-center gap-4">
                    <div>
                        <p className="font-medium">Установить приложение</p>
                        <p className="text-sm opacity-90">Быстрый доступ без браузера</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setShowInstall(false)}
                            className="px-4 py-2 text-gray-400 hover:text-white transition"
                        >
                            Позже
                        </button>
                        <button
                            type="button"
                            onClick={() => void onInstall()}
                            className="bg-white text-gray-900 px-4 py-2 rounded font-medium hover:bg-gray-100 transition"
                        >
                            Установить
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}